import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Collapse, Paper, Stack, Typography, useMediaQuery } from '@mui/material'
import { AddPhotoAlternate } from '@mui/icons-material'
import { useAdminListarRecorteCatalogoQuery, type GrupoRecorte, type ItemRecorte } from '../../api'
import { MAX_BANNERS, dataDeHoje, referenciaBanner, situacaoBanner, type BannerRascunho, type CatalogoDoAdmin, type DestinoBannerRascunho } from '../../lib/banners'
import { uuidLocal } from '../../lib/id'
import { DialogConfirmar } from './dialog-confirmar'
import { PainelBanner, rotuloGrupo, rotuloProduto, type OpcaoGrupo, type OpcaoProduto } from './painel-banner'
import { ResumoBanner } from './resumo-banner'
import { useUploadImagem } from './use-upload-imagem'

/** Pedido da tela para abrir um banner; `vez` muda a cada pedido, porque o mesmo banner pode precisar abrir de novo. */
export interface PedidoAbrirBanner {
  idBanner: string
  vez: number
}

interface EditorBannersProps {
  idConfig: number
  valor: BannerRascunho[] | null | undefined
  onChange: (banners: BannerRascunho[]) => void
  /** O "Salvar" barrado por um banner: ele abre e a tela rola até ele. */
  abrirBanner?: PedidoAbrirBanner | null
  /** A lista na tela é diferente da que está gravada: as etiquetas ainda não valem para o cardápio. */
  naoSalvo?: boolean
}

/** Barra fixa do topo do admin (64px) mais um respiro: linha mais alta que isso está escondida debaixo dela. */
const FOLGA_TOPO_PX = 80

interface Ocultos {
  grupos: Set<number>
  produtos: Set<number>
}

/** Só conta como oculto o que está fora da loja inteira; oculto em parte ainda aparece em algum canal. */
function ocultosEmTudo(ocultos: ItemRecorte[]): Ocultos {
  const daLojaInteira = ocultos.filter((o) => o.canal === '*' && o.idIdentificador === 0)
  return {
    grupos: new Set(daLojaInteira.filter((o) => o.tipo === 'GRUPO').map((o) => o.idReferencia)),
    produtos: new Set(daLojaInteira.filter((o) => o.tipo === 'PRODUTO').map((o) => o.idReferencia)),
  }
}

/** Produto em mais de um grupo aparece uma vez só, e só fica oculto se estiver oculto em todos eles. */
function opcoesProduto(grupos: GrupoRecorte[], ocultos: Ocultos): OpcaoProduto[] {
  const porId = new Map<number, OpcaoProduto>()
  for (const grupo of grupos) {
    for (const produto of grupo.produtos) {
      const ocultoAqui = ocultos.grupos.has(grupo.id) || ocultos.produtos.has(produto.id)
      const existente = porId.get(produto.id)
      if (existente) {
        existente.oculto = existente.oculto && ocultoAqui
        continue
      }
      porId.set(produto.id, {
        id: produto.id,
        descricao: produto.descricao,
        pausado: produto.pausado,
        oculto: ocultoAqui,
        grupo: grupo.descricao,
      })
    }
  }
  return [...porId.values()]
}

/** Destino na linha de resumo; enquanto o catálogo carrega, ainda não há o nome do produto ou do grupo. */
function resumoDestino(
  destino: DestinoBannerRascunho,
  catalogoCarregado: boolean,
  produto: OpcaoProduto | null,
  grupo: OpcaoGrupo | null,
): string {
  switch (destino.tipo) {
    case 'PRODUTO':
      if (destino.idProduto === null) return 'Falta escolher o produto'
      if (produto) return `Abre: ${rotuloProduto(produto)}`
      return catalogoCarregado ? 'Abre: um produto que saiu do cadastro' : 'Abre: um produto'
    case 'GRUPO':
      if (destino.idGrupo === null) return 'Falta escolher o grupo'
      if (grupo) return `Abre: grupo ${rotuloGrupo(grupo)}`
      return catalogoCarregado ? 'Abre: um grupo que saiu do cadastro' : 'Abre: um grupo'
    case 'PROMOCOES':
      return 'Abre: as promoções (quando houver)'
    default:
      return 'Só a imagem'
  }
}

/** Lista dos banners do topo do cardápio, uma linha por banner e um aberto por vez; grava junto com o "Salvar alterações". */
export function EditorBanners({ idConfig, valor, onChange, abrirBanner, naoSalvo = false }: EditorBannersProps): JSX.Element {
  const lista = valor ?? []
  const cheio = lista.length >= MAX_BANNERS
  const hoje = dataDeHoje()
  const prefixo = useId()
  const reduzirMovimento = useMediaQuery('(prefers-reduced-motion: reduce)')
  const { data: recorte, isFetching, isError } = useAdminListarRecorteCatalogoQuery(idConfig, { skip: lista.length === 0 })
  const ocultos = useMemo(() => ocultosEmTudo(recorte?.ocultos ?? []), [recorte])
  const grupos = useMemo<OpcaoGrupo[]>(
    () => (recorte?.grupos ?? []).map((g) => ({ id: g.id, descricao: g.descricao, oculto: ocultos.grupos.has(g.id) })),
    [recorte, ocultos],
  )
  const produtos = useMemo(() => opcoesProduto(recorte?.grupos ?? [], ocultos), [recorte, ocultos])
  const produtoPorId = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos])
  const grupoPorId = useMemo(() => new Map(grupos.map((g) => [g.id, g])), [grupos])
  const catalogo = useMemo<CatalogoDoAdmin | null>(
    () => (recorte ? { produtos: produtoPorId, grupos: grupoPorId } : null),
    [recorte, produtoPorId, grupoPorId],
  )
  const [aberto, setAberto] = useState<string | null>(null)
  const [remocao, setRemocao] = useState({ id: '', titulo: '', aberta: false })

  // O upload termina segundos depois do clique: a alteração parte da lista de agora, não da daquele clique.
  const listaAtual = useRef(lista)
  useEffect(() => {
    listaAtual.current = lista
  })

  const linhas = useRef(new Map<string, HTMLElement>())
  const ancoraRolagem = useRef<{ id: string; topo: number } | null>(null)
  const rolarQuandoAbrir = useRef<string | null>(null)

  // Depois de subir ou descer, a linha movida volta para baixo do ponteiro: o segundo clique na seta continua nela.
  useLayoutEffect(() => {
    const ancora = ancoraRolagem.current
    if (!ancora) return
    ancoraRolagem.current = null

    const linha = linhas.current.get(ancora.id)
    const diferenca = linha ? linha.getBoundingClientRect().top - ancora.topo : 0
    if (diferenca !== 0 && typeof window.scrollBy === 'function') window.scrollBy(0, diferenca)
  })

  /** Rola só quando a linha está escondida debaixo da barra do topo ou abaixo do fim da janela. */
  function rolarSeForaDaVista(id: string): void {
    const item = linhas.current.get(id)
    const linha = item?.firstElementChild
    // O jsdom dos testes não tem `scrollIntoView`.
    if (!item || !(linha instanceof HTMLElement) || typeof item.scrollIntoView !== 'function') return

    const { top, bottom } = linha.getBoundingClientRect()
    if (top >= FOLGA_TOPO_PX && bottom <= window.innerHeight) return
    item.scrollIntoView({ behavior: reduzirMovimento ? 'auto' : 'smooth', block: 'start' })
  }

  // Toda abertura marca a rolagem: a última vence, e um pedido velho da tela não rola por cima de um toque novo.
  function abrir(id: string): void {
    rolarQuandoAbrir.current = id
    setAberto(id)
  }

  useEffect(() => {
    if (!abrirBanner) return
    if (aberto === abrirBanner.idBanner) rolarSeForaDaVista(abrirBanner.idBanner)
    else abrir(abrirBanner.idBanner)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrirBanner])

  function alterar(id: string, parcial: Partial<BannerRascunho>): void {
    onChange(listaAtual.current.map((banner) => (banner.id === id ? { ...banner, ...parcial } : banner)))
  }

  function mover(id: string, deslocamento: -1 | 1): void {
    const nova = [...listaAtual.current]
    const de = nova.findIndex((banner) => banner.id === id)
    const para = de + deslocamento
    const banner = nova[de]
    const vizinho = nova[para]
    if (!banner || !vizinho) return

    nova[para] = banner
    nova[de] = vizinho
    const linha = linhas.current.get(id)
    ancoraRolagem.current = linha ? { id, topo: linha.getBoundingClientRect().top } : null
    onChange(nova)
  }

  function confirmarRemocao(): void {
    onChange(listaAtual.current.filter((banner) => banner.id !== remocao.id))
    setRemocao((atual) => ({ ...atual, aberta: false }))
  }

  // O banner só nasce com a imagem já enviada, e já abre para o lojista dizer para onde ele leva.
  const novaImagem = useUploadImagem('capa', (url) => {
    if (!url || listaAtual.current.length >= MAX_BANNERS) return
    const id = uuidLocal()
    onChange([...listaAtual.current, { id, urlImagem: url, nome: null, destino: { tipo: 'NENHUM' }, inicio: null, fim: null }])
    abrir(id)
  })

  return (
    <Stack spacing={2}>
      {isError && (
        <Alert severity="warning">
          Não deu para conferir os produtos agora. Recarregue a página para ver a situação dos banners.
        </Alert>
      )}

      {naoSalvo && (
        <Alert severity="info">Os banners mudaram e ainda não estão no cardápio. Clique em Salvar alterações.</Alert>
      )}

      {lista.length === 0 ? (
        <Box sx={{ p: 2, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum banner ainda. Enquanto não houver, o topo do cardápio continua como está.
          </Typography>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {lista.map((banner, indice) => {
            const numero = indice + 1
            const destino = banner.destino
            const idPainel = `${prefixo}-painel-${banner.id}`
            const opcaoProduto =
              destino.tipo === 'PRODUTO' && destino.idProduto !== null ? produtoPorId.get(destino.idProduto) ?? null : null
            const opcaoGrupo = destino.tipo === 'GRUPO' && destino.idGrupo !== null ? grupoPorId.get(destino.idGrupo) ?? null : null

            return (
              <Box
                key={banner.id}
                ref={(elemento: HTMLDivElement | null) => {
                  if (elemento) linhas.current.set(banner.id, elemento)
                  else linhas.current.delete(banner.id)
                }}
                // Margem para o "rolar até o banner" não parar debaixo da barra fixa do topo.
                sx={{ borderTop: indice > 0 ? 1 : 0, borderColor: 'divider', scrollMarginTop: `${FOLGA_TOPO_PX}px` }}
              >
                <ResumoBanner
                  banner={banner}
                  numero={numero}
                  total={lista.length}
                  textoDestino={resumoDestino(destino, Boolean(catalogo), opcaoProduto, opcaoGrupo)}
                  situacao={situacaoBanner(banner, catalogo, hoje)}
                  hoje={hoje}
                  aberto={aberto === banner.id}
                  idPainel={idPainel}
                  aoAlternar={() => (aberto === banner.id ? setAberto(null) : abrir(banner.id))}
                  aoMover={(deslocamento) => mover(banner.id, deslocamento)}
                  aoRemover={() =>
                    setRemocao({ id: banner.id, titulo: `Remover o banner ${referenciaBanner(banner, numero)}?`, aberta: true })
                  }
                />

                {/* Fechado, o painel continua montado (e escondido): um "Trocar imagem" em andamento não perde o erro. */}
                <Collapse
                  id={idPainel}
                  in={aberto === banner.id}
                  onEntered={() => {
                    if (rolarQuandoAbrir.current !== banner.id) return
                    rolarQuandoAbrir.current = null
                    rolarSeForaDaVista(banner.id)
                  }}
                >
                  <PainelBanner
                    banner={banner}
                    hoje={hoje}
                    produtos={produtos}
                    grupos={grupos}
                    produtoEscolhido={opcaoProduto}
                    grupoEscolhido={opcaoGrupo}
                    catalogoCarregado={Boolean(catalogo)}
                    carregando={isFetching}
                    aoAlterar={(parcial) => alterar(banner.id, parcial)}
                  />
                </Collapse>
              </Box>
            )
          })}
        </Paper>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Button
          variant="outlined"
          startIcon={novaImagem.enviando ? <CircularProgress size={16} /> : <AddPhotoAlternate />}
          onClick={novaImagem.abrirSeletor}
          disabled={cheio || novaImagem.enviando}
          sx={{ flexShrink: 0 }}
        >
          Adicionar banner
        </Button>
        <Typography variant="body2" color="text.secondary">
          {cheio
            ? `Limite de ${MAX_BANNERS} banners. Remova um para adicionar outro.`
            : `${lista.length} de ${MAX_BANNERS}. Primeiro você escolhe a imagem; depois, para onde ela leva. No cardápio, eles passam nesta ordem.`}
        </Typography>
        {novaImagem.inputArquivo}
      </Stack>

      {novaImagem.erro && <Alert severity="error">{novaImagem.erro}</Alert>}

      {/* O texto fica guardado ao fechar: sem isso o título mudaria durante a animação de saída. */}
      <DialogConfirmar
        aberto={remocao.aberta}
        titulo={remocao.titulo}
        descricao="A imagem, o link e o período deste banner saem da lista. O cardápio só muda quando você clicar em Salvar alterações."
        textoConfirmar="Remover"
        aoConfirmar={confirmarRemocao}
        aoCancelar={() => setRemocao((atual) => ({ ...atual, aberta: false }))}
      />
    </Stack>
  )
}
